import {store} from '../../store';
import {getLocaleText} from '../../util/translation';
import {render} from '../../reducers/rootReducer';
import {handleTokenResult} from './square';
import {type GooglePay, type Payments} from '@square/web-payments-sdk-types';
import {type SquareGateway, type ProcessedTokenResult} from '../../models/square';
import googlepayBadgeURL from '../../../img/badge/google-pay.svg';
import googlepayImageURL from '../../../img/marks/google-pay.svg';
import {GatewayEligibility, type GatewayConfiguration} from '../../models/GatewayConfiguration';
import {Feature} from '../../reducers/environmentReducer';
import {FeatureFlag} from '../../../../@type/features';
import {captureSentryException} from '../../../../@shared/ts/sentry';
import {type PaymentRequest} from '@square/web-payments-sdk-types';
import {debounce} from '../../util/debounce';
import {PaymentConfiguration} from '../../reducers/paymentConfigurationReducer';
import {type TransactionUpdate} from '../../../../@shared/ts/transaction';
import {getErrorName} from '../../../../@shared/ts/error';

// https://developer.squareup.com/reference/sdks/web/payments/digital-wallets/google-pay
function initSquareGooglePayMethod(): SquareGateway {
	let gpay: GooglePay | null = null;
	let buttonInstanceWaitingInterval: NodeJS.Timer | undefined;

	const config: GatewayConfiguration = {
		gatewayId: 'peachpay_square_googlepay',
		name: Feature.metadata<string>(FeatureFlag.SQUARE_GOOGLEPAY_GATEWAY, 'title') ?? getLocaleText('Google Pay'),
		description: getDescription(),
		assets: {
			badge: {src: googlepayBadgeURL},
			title: {src: googlepayImageURL},
		},
		hasCustomButton: true,
	};

	const debouncedActivate = debounce((async (payments: Payments, paymentRequest: PaymentRequest) => {
		if (gpay) {
			return;
		}

		if (PaymentConfiguration.eligibleGateway(config.gatewayId) !== GatewayEligibility.Eligible) {
			return;
		}

		try {
			gpay = await payments.googlePay(paymentRequest);
			store.dispatch(render());

			config.initialized = true;
			store.dispatch(render());
		} catch (error: unknown) {
			config.initialized = false;
			if (getErrorName(error) === 'PaymentMethodUnsupportedError') {
				config.browser = false;
			}

			store.dispatch(render());

			console.error(error);
			captureSentryException(error as Error, {
				paymentRequest,
			});
		}
	}) satisfies SquareGateway['activate']);

	return {
		config,
		activate: debouncedActivate,
		reset() {
			clearInterval(buttonInstanceWaitingInterval);

			const oldInstance = gpay;
			gpay = null;

			oldInstance?.destroy().catch(error => {
				if (getErrorName(error) === 'PaymentMethodAlreadyDestroyedError') {
					return;
				}

				console.error(error);
				captureSentryException(error as Error);
			});
		},
		attach(selector: string) {
			clearInterval(buttonInstanceWaitingInterval);
			buttonInstanceWaitingInterval = setInterval(() => {
				if (gpay) {
					clearInterval(buttonInstanceWaitingInterval);

					gpay?.attach(selector, {
						buttonColor: 'black',
						buttonSizeMode: 'fill',
					})?.catch(error => {
						config.initialized = false;
						store.dispatch(render());

						if (getErrorName(error) === 'PaymentMethodUnsupportedError') {
							console.log(error);
							return;
						}

						console.error(error);
						captureSentryException(error as Error, {selector});
					});
				}
			}, 200);
		},
		detach() {
			clearInterval(buttonInstanceWaitingInterval);
			gpay?.detach()?.catch(error => {
				if (getErrorName(error) === 'PaymentMethodNotAttachedError') {
					return;
				}

				config.initialized = false;
				store.dispatch(render());

				if (getErrorName(error) === 'PaymentMethodAlreadyDestroyedError') {
					gpay = null;
					return;
				}

				console.error(error);
				captureSentryException(error as Error);
			});
		},
		async tokenize(): Promise<ProcessedTokenResult> {
			try {
				if (!gpay) {
					return [null, null, {
						paymentStatus: 'creationfailed',
						note: 'Failed to find Google Pay instance while attempting to tokenize the payment.',
					}];
				}

				const result = await gpay.tokenize();
				return handleTokenResult(result);
			} catch (error: unknown) {
				const update: TransactionUpdate = {
					paymentStatus: 'creationfailed',
					note: 'An unknown exception occured while tokenizing the payment.',
				};

				if (error instanceof Error) {
					update.note = error.message;
				}

				return [null, null, update];
			}
		},
	};
}

function getDescription(): string {
	return /* html */ `
<img style="display: block; text-align: left; height: 1.5rem; " src="${googlepayImageURL}">
<p style="text-align: left; margin: 0.5rem 0;">${getLocaleText('Google Pay selected for checkout.')}</p>
<hr/>
<p style="text-align: left; margin: 0.5rem 0 0;" class="muted">${getLocaleText('Another step will appear after submitting your order to complete your purchase details.')}<p>`;
}

export {initSquareGooglePayMethod};
